import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Camera, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { BugReport } from '../../services/bugReportService';
import { CriticidadeBadge } from './CriticidadeBadge';
import { StatusBadge } from './StatusBadge';

interface Props {
  bugReports: BugReport[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  total: number;
  onRowClick: (bugReport: BugReport) => void;
  onPageChange: (page: number) => void;
}

export const BugReportTable: React.FC<Props> = ({
  bugReports,
  loading,
  currentPage,
  totalPages,
  total,
  onRowClick,
  onPageChange,
}) => {
  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2" />
        Carregando ocorrencias...
      </div>
    );
  }

  if (bugReports.length === 0) {
    return (
      <div className="p-12 text-center">
        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-medium">Nenhuma ocorrencia encontrada</p>
        <p className="text-xs text-gray-400 mt-1">Ajuste os filtros ou aguarde novos reports</p>
      </div>
    );
  }

  const formatDate = (s: string) => {
    try {
      return format(new Date(s), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR });
    } catch {
      return s;
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Titulo</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Categoria</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Criticidade</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Origem</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Hospital</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Reportado por</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Data</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {bugReports.map((br) => (
              <tr
                key={br.id}
                onClick={() => onRowClick(br)}
                className={`cursor-pointer hover:bg-blue-50 transition-colors ${
                  br.criticidade === 'CRITICA' ? 'bg-red-50/30' : ''
                }`}
              >
                <td className="px-4 py-3 text-sm font-mono text-gray-500">#{br.id}</td>
                <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                  <div className="flex items-center gap-2">
                    {br.foto_url && <Camera className="w-4 h-4 text-blue-500" />}
                    <span className="line-clamp-1 max-w-xs">{br.titulo}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-gray-700">{br.categoria_nome}</td>
                <td className="px-4 py-3"><CriticidadeBadge criticidade={br.criticidade} size="sm" /></td>
                <td className="px-4 py-3"><StatusBadge status={br.status} size="sm" /></td>
                <td className="px-4 py-3 text-xs text-gray-600">{br.origem}</td>
                <td className="px-4 py-3 text-xs text-gray-700 max-w-[200px] truncate">{br.hospital_nome || '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-700">{br.reportado_por_nome || '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(br.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginacao */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
          <span className="text-xs text-gray-600">
            Pagina {currentPage} de {totalPages} ({total} ocorrencia{total !== 1 ? 's' : ''})
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
